import { API_BASE_URL } from '../config/settings';

interface AppSettings {
  fields: Array<{
    id: string;
    label: string;
    type: string;
    layout: string;
    options?: string[];
    defaultVisible?: boolean;
  }>;
  areas: string[];
  resourceCategories: string[];
  platforms?: string[];
  contentFormats?: string[];
}

interface IngestDocs {
  endpoint: string;
  method: string;
  headers: Record<string, string>;
  body: Record<string, unknown>;
  examples: Record<string, unknown>;
  urlTypeDetection: Record<string, string>;
}

interface IngestStats {
  total: number;
  prefilled: number;
  manual: number;
  lastIngest: number;
}

interface IngestPendingItem {
  id: string;
  title?: string;
  sourceUrl?: string;
  status: string;
  createdAt: number;
  error?: string;
}

interface IngestLogEntry {
  timestamp: number;
  headers: Record<string, string>;
  body: unknown;
  result: { success: boolean; error?: string; id?: string };
}

export class Settings {
  private modalEl: HTMLElement | null = null;
  private settings: AppSettings | null = null;
  private ingestDocs: IngestDocs | null = null;
  private ingestStats: IngestStats | null = null;
  private ingestPending: IngestPendingItem[] = [];
  private ingestLogs: IngestLogEntry[] = [];
  private activeTab: 'categories' | 'fields' | 'ingest' = 'categories';
  private isRetraining: boolean = false;
  private isPurging: boolean = false;

  constructor() {}

  async show(): Promise<void> {
    // Load settings and ingest docs
    await Promise.all([
      this.loadSettings(),
      this.loadIngestDocs(),
      this.loadIngestStats(),
      this.loadIngestPending(),
      this.loadIngestLogs()
    ]);

    this.render();
  }

  private async loadSettings(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`);
      const data = await response.json();
      if (data.success) {
        this.settings = data.data;
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }

  private async loadIngestDocs(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/ingest-docs`);
      const data = await response.json();
      if (data.success) {
        this.ingestDocs = data.data;
      }
    } catch (error) {
      console.error('Failed to load ingest docs:', error);
    }
  }

  private async loadIngestStats(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/ingest/stats`);
      const data = await response.json();
      if (data.success) {
        this.ingestStats = data.data;
      }
    } catch (error) {
      console.error('Failed to load ingest stats:', error);
    }
  }

  private async loadIngestPending(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/ingest/pending`);
      const data = await response.json();
      if (data.success) {
        this.ingestPending = data.data.items || [];
      }
    } catch (error) {
      console.error('Failed to load ingest pending:', error);
    }
  }

  private async loadIngestLogs(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/ingest/logs`);
      const data = await response.json();
      if (data.success) {
        this.ingestLogs = data.data.logs || [];
      }
    } catch (error) {
      console.error('Failed to load ingest logs:', error);
    }
  }

  private render(): void {
    // Remove existing modal if any
    this.modalEl?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'settings-modal-overlay';
    overlay.innerHTML = `
      <div class="settings-modal">
        <div class="settings-header">
          <h2>Settings</h2>
          <button class="settings-close-btn">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="settings-tabs">
          <button class="settings-tab ${this.activeTab === 'categories' ? 'active' : ''}" data-tab="categories">
            <span class="material-symbols-outlined">category</span>
            Categories
          </button>
          <button class="settings-tab ${this.activeTab === 'fields' ? 'active' : ''}" data-tab="fields">
            <span class="material-symbols-outlined">tune</span>
            Fields
          </button>
          <button class="settings-tab ${this.activeTab === 'ingest' ? 'active' : ''}" data-tab="ingest">
            <span class="material-symbols-outlined">code</span>
            Ingest API
          </button>
        </div>

        <div class="settings-content">
          ${this.renderTabContent()}
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.modalEl = overlay;

    this.setupEventListeners();
  }

  private renderTabContent(): string {
    switch (this.activeTab) {
      case 'categories':
        return this.renderCategoriesTab();
      case 'fields':
        return this.renderFieldsTab();
      case 'ingest':
        return this.renderIngestTab();
      default:
        return '';
    }
  }

  private renderCategoriesTab(): string {
    if (!this.settings) return '<p>Loading...</p>';

    return `
      <div class="settings-section">
        <h3>Resource Categories</h3>
        <p class="settings-hint">Categories for organizing resources. Used by AI classification.</p>
        <div class="settings-list" id="categories-list">
          ${this.settings.resourceCategories.map(cat => `
            <div class="settings-item">
              <span>${cat}</span>
              <button class="settings-item-delete" data-type="category" data-value="${cat}">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          `).join('')}
        </div>
        <div class="settings-add-row">
          <input type="text" id="new-category-input" placeholder="New category...">
          <button id="add-category-btn" class="settings-add-btn">Add</button>
        </div>
      </div>

      <div class="settings-section">
        <h3>Platforms</h3>
        <p class="settings-hint">Source platforms for content.</p>
        <div class="settings-list" id="platforms-list">
          ${(this.settings.platforms || []).map(plat => `
            <div class="settings-item">
              <span>${plat}</span>
              <button class="settings-item-delete" data-type="platform" data-value="${plat}">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          `).join('')}
        </div>
        <div class="settings-add-row">
          <input type="text" id="new-platform-input" placeholder="New platform...">
          <button id="add-platform-btn" class="settings-add-btn">Add</button>
        </div>
      </div>

      <div class="settings-section">
        <h3>Content Formats</h3>
        <p class="settings-hint">Types of content formats.</p>
        <div class="settings-list" id="formats-list">
          ${(this.settings.contentFormats || []).map(fmt => `
            <div class="settings-item">
              <span>${fmt}</span>
              <button class="settings-item-delete" data-type="format" data-value="${fmt}">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          `).join('')}
        </div>
        <div class="settings-add-row">
          <input type="text" id="new-format-input" placeholder="New format...">
          <button id="add-format-btn" class="settings-add-btn">Add</button>
        </div>
      </div>

      <div class="settings-section">
        <h3>PARA Areas</h3>
        <p class="settings-hint">Areas of responsibility (PARA methodology).</p>
        <div class="settings-list" id="areas-list">
          ${this.settings.areas.map(area => `
            <div class="settings-item">
              <span>${area}</span>
              <button class="settings-item-delete" data-type="area" data-value="${area}">
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          `).join('')}
        </div>
        <div class="settings-add-row">
          <input type="text" id="new-area-input" placeholder="New area...">
          <button id="add-area-btn" class="settings-add-btn">Add</button>
        </div>
      </div>
    `;
  }

  private renderFieldsTab(): string {
    if (!this.settings) return '<p>Loading...</p>';

    return `
      <div class="settings-section">
        <h3>Document Fields</h3>
        <p class="settings-hint">Fields available in the document info panel.</p>
        <div class="settings-fields-table">
          <div class="settings-fields-header">
            <span>Field</span>
            <span>Type</span>
            <span>Layout</span>
            <span>Options</span>
          </div>
          ${this.settings.fields.map(field => `
            <div class="settings-field-row">
              <span class="field-label">${field.label}</span>
              <span class="field-type">${field.type}</span>
              <span class="field-layout">${field.layout}</span>
              <span class="field-options">${field.options ? field.options.length + ' options' : '-'}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private renderIngestTab(): string {
    if (!this.ingestDocs) return '<p>Loading...</p>';

    return `
      <div class="settings-section">
        <h3>Request Log (${this.ingestLogs.length})</h3>
        <p class="settings-hint">Requests to /api/ingest/debug</p>
        ${this.ingestLogs.length === 0 ? '<p class="settings-hint">No requests logged yet</p>' : ''}
        <div class="ingest-logs-list">
          ${this.ingestLogs.slice(0, 20).map(log => `
            <div class="ingest-log-entry ${log.result.success ? 'success' : 'error'}">
              <div class="log-header">
                <span class="log-time">${this.formatDate(log.timestamp)}</span>
                <span class="log-status ${log.result.success ? 'success' : 'error'}">${log.result.success ? 'OK' : 'FAIL'}</span>
              </div>
              <div class="log-body">
                <pre>${JSON.stringify(log.body, null, 2)}</pre>
              </div>
              ${log.result.error ? `<div class="log-error">${log.result.error}</div>` : ''}
            </div>
          `).join('')}
        </div>
      </div>

      <div class="settings-section">
        <h3>Ingest API Endpoint</h3>
        <p class="settings-hint">Use this endpoint to programmatically add content to your knowledge base.</p>

        <div class="ingest-endpoint">
          <code class="endpoint-url">${this.ingestDocs.method} ${this.ingestDocs.endpoint}</code>
          <button class="copy-btn" data-copy="${this.ingestDocs.endpoint}">
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>

        <h4>Headers</h4>
        <pre class="ingest-code">${JSON.stringify(this.ingestDocs.headers, null, 2)}</pre>

        <h4>Request Body</h4>
        <pre class="ingest-code">${JSON.stringify(this.ingestDocs.body, null, 2)}</pre>

        <h4>Examples</h4>

        <div class="ingest-example">
          <h5>Direct Content</h5>
          <pre class="ingest-code">${JSON.stringify(this.ingestDocs.examples.directContent, null, 2)}</pre>
          <button class="copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.directContent)}'>
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>

        <div class="ingest-example">
          <h5>YouTube Transcription</h5>
          <pre class="ingest-code">${JSON.stringify(this.ingestDocs.examples.youtubeTranscription, null, 2)}</pre>
          <button class="copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.youtubeTranscription)}'>
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>

        <div class="ingest-example">
          <h5>Web Extraction</h5>
          <pre class="ingest-code">${JSON.stringify(this.ingestDocs.examples.webExtraction, null, 2)}</pre>
          <button class="copy-btn" data-copy='${JSON.stringify(this.ingestDocs.examples.webExtraction)}'>
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>

        <h4>URL Type Detection</h4>
        <div class="url-detection-table">
          ${Object.entries(this.ingestDocs.urlTypeDetection).map(([url, type]) => `
            <div class="url-detection-row">
              <code>${url}</code>
              <span>${type}</span>
            </div>
          `).join('')}
        </div>

        <h4>Debug Endpoint</h4>
        <div class="ingest-endpoint debug-endpoint">
          <code class="endpoint-url">POST ${(this.ingestDocs as any).debugEndpoint?.url || '/api/ingest/debug'}</code>
          <button class="copy-btn" data-copy="${(this.ingestDocs as any).debugEndpoint?.url || '/api/ingest/debug'}">
            <span class="material-symbols-outlined">content_copy</span>
          </button>
        </div>
        <p class="settings-hint">
          Send the same request you would send to /api/ingest, and this endpoint will show you exactly what was received without any validation or processing.
        </p>
      </div>
    `;
  }

  private setupEventListeners(): void {
    if (!this.modalEl) return;

    // Close button
    this.modalEl.querySelector('.settings-close-btn')?.addEventListener('click', () => this.close());

    // Overlay click
    this.modalEl.addEventListener('click', (e) => {
      if (e.target === this.modalEl) this.close();
    });

    // Tab switching
    this.modalEl.querySelectorAll('.settings-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        this.activeTab = tab.getAttribute('data-tab') as 'categories' | 'fields' | 'ingest';
        this.render();
      });
    });

    // Copy buttons
    this.modalEl.querySelectorAll('.copy-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const text = btn.getAttribute('data-copy') || '';
        navigator.clipboard.writeText(text);
        this.showNotification('Copied to clipboard');
      });
    });

    // Delete buttons
    this.modalEl.querySelectorAll('.settings-item-delete').forEach(btn => {
      btn.addEventListener('click', async () => {
        const type = btn.getAttribute('data-type');
        const value = btn.getAttribute('data-value');
        if (type && value) {
          await this.deleteItem(type, value);
        }
      });
    });

    // Add category
    this.modalEl.querySelector('#add-category-btn')?.addEventListener('click', () => {
      const input = this.modalEl?.querySelector('#new-category-input') as HTMLInputElement;
      if (input?.value.trim()) {
        this.addItem('category', input.value.trim());
      }
    });

    // Add platform
    this.modalEl.querySelector('#add-platform-btn')?.addEventListener('click', () => {
      const input = this.modalEl?.querySelector('#new-platform-input') as HTMLInputElement;
      if (input?.value.trim()) {
        this.addItem('platform', input.value.trim());
      }
    });

    // Add format
    this.modalEl.querySelector('#add-format-btn')?.addEventListener('click', () => {
      const input = this.modalEl?.querySelector('#new-format-input') as HTMLInputElement;
      if (input?.value.trim()) {
        this.addItem('format', input.value.trim());
      }
    });

    // Add area
    this.modalEl.querySelector('#add-area-btn')?.addEventListener('click', () => {
      const input = this.modalEl?.querySelector('#new-area-input') as HTMLInputElement;
      if (input?.value.trim()) {
        this.addItem('area', input.value.trim());
      }
    });

    // Enter key on inputs
    this.modalEl.querySelectorAll('.settings-add-row input').forEach(input => {
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') {
          const btn = input.parentElement?.querySelector('.settings-add-btn') as HTMLButtonElement;
          btn?.click();
        }
      });
    });

    // Ingest actions
    this.modalEl.querySelector('#ingest-retrain-btn')?.addEventListener('click', () => {
      this.retrainClassifier();
    });

    this.modalEl.querySelector('#ingest-purge-btn')?.addEventListener('click', () => {
      if (confirm('Are you sure you want to purge ALL knowledge items? This cannot be undone.')) {
        this.purgeKnowledge();
      }
    });
  }

  private async addItem(type: string, value: string): Promise<void> {
    if (!this.settings) return;

    let updated = false;
    switch (type) {
      case 'category':
        if (!this.settings.resourceCategories.includes(value)) {
          this.settings.resourceCategories.push(value);
          updated = true;
        }
        break;
      case 'platform':
        if (!this.settings.platforms?.includes(value)) {
          this.settings.platforms = this.settings.platforms || [];
          this.settings.platforms.push(value);
          updated = true;
        }
        break;
      case 'format':
        if (!this.settings.contentFormats?.includes(value)) {
          this.settings.contentFormats = this.settings.contentFormats || [];
          this.settings.contentFormats.push(value);
          updated = true;
        }
        break;
      case 'area':
        if (!this.settings.areas.includes(value)) {
          this.settings.areas.push(value);
          updated = true;
        }
        break;
    }

    if (updated) {
      await this.saveSettings();
      this.render();
    }
  }

  private async deleteItem(type: string, value: string): Promise<void> {
    if (!this.settings) return;

    switch (type) {
      case 'category':
        this.settings.resourceCategories = this.settings.resourceCategories.filter(c => c !== value);
        break;
      case 'platform':
        this.settings.platforms = (this.settings.platforms || []).filter(p => p !== value);
        break;
      case 'format':
        this.settings.contentFormats = (this.settings.contentFormats || []).filter(f => f !== value);
        break;
      case 'area':
        this.settings.areas = this.settings.areas.filter(a => a !== value);
        break;
    }

    await this.saveSettings();
    this.render();
  }

  private async saveSettings(): Promise<void> {
    if (!this.settings) return;

    try {
      await fetch(`${API_BASE_URL}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.settings)
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  }

  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'settings-notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }

  private formatDate(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }

  private async retrainClassifier(): Promise<void> {
    if (this.isRetraining) return;
    this.isRetraining = true;
    this.render();

    try {
      const response = await fetch(`${API_BASE_URL}/ingest/retrain`, { method: 'POST' });
      const data = await response.json();

      if (data.success) {
        this.showNotification(`Retrained with ${data.data?.examplesUsed || 0} examples`);
      } else {
        this.showNotification('Retrain failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      this.showNotification('Retrain failed');
      console.error('Retrain error:', error);
    }

    this.isRetraining = false;
    this.render();
  }

  private async purgeKnowledge(): Promise<void> {
    if (this.isPurging) return;
    this.isPurging = true;
    this.render();

    try {
      const response = await fetch(`${API_BASE_URL}/ingest/purge`, { method: 'DELETE' });
      const data = await response.json();

      if (data.success) {
        this.showNotification(`Purged ${data.data?.deleted || 0} items`);
        // Reload stats
        await Promise.all([this.loadIngestStats(), this.loadIngestPending()]);
      } else {
        this.showNotification('Purge failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      this.showNotification('Purge failed');
      console.error('Purge error:', error);
    }

    this.isPurging = false;
    this.render();
  }

  close(): void {
    this.modalEl?.remove();
    this.modalEl = null;
  }
}
